"use client";

import * as Sentry from "@sentry/browser";
import posthog from "posthog-js";

/**
 * Frontend telemetry: Sentry error tracking + PostHog product analytics.
 *
 * Both are opt-in via env vars and no-op when unset, so local dev and
 * preview builds emit nothing:
 * - NEXT_PUBLIC_SENTRY_DSN
 * - NEXT_PUBLIC_POSTHOG_KEY (+ optional NEXT_PUBLIC_POSTHOG_HOST)
 */

const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN || "";
// Baked in at build time (next.config.ts). Must match the `--release` used by
// the CI source-map upload so Sentry can symbolicate production stack traces.
const SENTRY_RELEASE = process.env.NEXT_PUBLIC_SENTRY_RELEASE || undefined;
const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || "";
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;

export function initTelemetry(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  if (SENTRY_DSN) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: process.env.NODE_ENV,
      release: SENTRY_RELEASE,
      tracesSampleRate: 0.1,
    });
  }

  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Pageviews are captured manually on route change (App Router SPA
      // navigations don't reload the page, so autocapture would only see
      // the first load).
      capture_pageview: false,
      capture_pageleave: true,
    });
  }
}

export function trackPageview(path: string): void {
  if (!initialized || !POSTHOG_KEY) return;
  posthog.capture("$pageview", { $current_url: window.location.origin + path });
}

/** Capture a product analytics event (no-op when PostHog is not configured). */
export function track(
  event: string,
  properties?: Record<string, unknown>
): void {
  if (!initialized || !POSTHOG_KEY) return;
  posthog.capture(event, properties);
}

/** Associate telemetry with the signed-in user. Id only — no PII. */
export function identifyUser(id: string): void {
  if (!initialized) return;
  if (POSTHOG_KEY) posthog.identify(id);
  if (SENTRY_DSN) Sentry.setUser({ id });
}

/** Report a handled error to Sentry (no-op when Sentry is not configured). */
export function captureError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (!initialized || !SENTRY_DSN) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
